import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Eraser, CheckCircle2, Square, Siren, LogOut } from 'lucide-react';
import { CandidatLink } from './candidat/CandidatLink';
import { AlerteFormateurDialog } from './candidat/AlerteFormateurDialog';
import { SuspicionDialog } from '../pages/formateur/journee/PresencesPage';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CHIP_CATEGORIES = [
  { key: 'positif', label: 'Comportement positif', dot: 'bg-emerald-500', chip: 'border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100', active: 'bg-emerald-600 border-emerald-600 text-white' },
  { key: 'progression', label: 'Progression', dot: 'bg-sky-500', chip: 'border-sky-300 text-sky-800 bg-sky-50 hover:bg-sky-100', active: 'bg-sky-600 border-sky-600 text-white' },
  { key: 'vigilance', label: 'Vigilance', dot: 'bg-amber-500', chip: 'border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100', active: 'bg-amber-500 border-amber-500 text-white' },
  { key: 'alerte', label: 'Alertes', dot: 'bg-red-500', chip: 'border-red-300 text-red-800 bg-red-50 hover:bg-red-100', active: 'bg-red-600 border-red-600 text-white' },
  { key: 'info', label: 'Infos', dot: 'bg-slate-400', chip: 'border-slate-300 text-slate-700 bg-slate-50 hover:bg-slate-100', active: 'bg-slate-600 border-slate-600 text-white' },
];

const PRESENCE = {
  present: { label: 'Présent', cls: 'text-emerald-600' },
  retard: { label: 'Retard', cls: 'text-amber-600' },
  absent: { label: 'Absent', cls: 'text-red-600' },
};

const appendPhrase = (texte, phrase) => {
  const t = (texte || '').trim();
  if (!t) return phrase;
  return `${t}${/[.!?]$/.test(t) ? '' : '.'} ${phrase}`;
};

export const ObservationsCandidatsPanel = ({ dossierId, date, candidats, observations, presences = [], onChange, canEdit, onRefresh }) => {
  const [chips, setChips] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [alertes, setAlertes] = useState([]);
  const [dialog, setDialog] = useState(null);
  const loadAlertes = () => { if (dossierId && date) axios.get(`${API_URL}/portail/formateur/journee/${dossierId}/${date}/alertes`).then(r => setAlertes(r.data)).catch(() => {}); };
  useEffect(loadAlertes, [dossierId, date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get(`${API_URL}/listes/chips-observation`).then(r => setChips(r.data.filter(c => c.actif !== false))).catch(() => {});
  }, []);

  const rows = useMemo(() => observations
    .map(o => ({ ...o, candidat: candidats.find(c => c.id === o.candidat_id) || { nom: '?', prenom: '?' }, presence: presences.find(p => p.candidat_id === o.candidat_id) }))
    .sort((a, b) => `${a.candidat.nom} ${a.candidat.prenom}`.localeCompare(`${b.candidat.nom} ${b.candidat.prenom}`)), [observations, candidats, presences]);

  useEffect(() => {
    if (rows.length && !rows.some(r => r.candidat_id === selectedId)) setSelectedId(rows[0].candidat_id);
  }, [rows, selectedId]);

  const idx = rows.findIndex(r => r.candidat_id === selectedId);
  const current = rows[idx];

  // Pré-remplissage automatique pour un absent sans observation
  useEffect(() => {
    if (!current || !canEdit || (current.texte || '').trim() || current.presence?.statut !== 'absent') return;
    const chip = chips.find(c => c.categorie === 'info' && c.libelle.toLowerCase() === 'absent');
    if (!chip) return;
    const motif = current.presence?.motif ? ` Motif : ${current.presence.motif}.` : '';
    onChange(current.candidat_id, `${chip.phrase}${motif}`);
  }, [current?.candidat_id, chips]); // eslint-disable-line react-hooks/exhaustive-deps

  if (rows.length === 0) return <p className="text-xs text-slate-400">Aucun candidat affecté</p>;

  const texte = current?.texte || '';
  const toggleChip = (chip) => {
    if (!canEdit || !current) return;
    if (texte.toLowerCase().includes(chip.phrase.toLowerCase())) return;
    onChange(current.candidat_id, appendPhrase(texte, chip.phrase));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(200px,1fr)_2fr] gap-4" data-testid="observations-panel">
      {dialog === 'alerte' && current && <AlerteFormateurDialog candidat={current.candidat} dossierId={dossierId} date={date} onClose={() => setDialog(null)} onDone={loadAlertes} />}
      {dialog === 'suspicion' && current && <SuspicionDialog candidat={current.candidat} dossierId={dossierId} date={date} onClose={() => setDialog(null)} onDone={() => { onRefresh?.(); }} />}
      <div className="flex md:flex-col gap-1.5 overflow-x-auto md:overflow-visible md:max-h-[520px] md:overflow-y-auto pr-1">
        {rows.map(r => {
          const saisie = !!(r.texte || '').trim();
          const active = r.candidat_id === selectedId;
          const pres = PRESENCE[r.presence?.statut];
          return (
            <button key={r.candidat_id} type="button" onClick={() => setSelectedId(r.candidat_id)}
              className={`text-left shrink-0 md:shrink rounded-lg border px-3 py-2 transition-colors ${active ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
              data-testid={`obs-candidat-${r.candidat_id}`}>
              <p className="text-sm font-medium text-slate-900 leading-tight whitespace-nowrap md:whitespace-normal">{r.candidat.nom} {r.candidat.prenom}</p>
              <p className="text-[11px] flex items-center gap-1.5 mt-0.5">
                {pres && <span className={`font-medium ${pres.cls}`}>{pres.label} ·</span>}
                {saisie ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Obs. saisie</span> : <span className="text-slate-400 flex items-center gap-1"><Square className="w-3 h-3" /> Sans observation</span>}
              </p>
            </button>
          );
        })}
      </div>

      {current && (
        <div className="space-y-3" data-testid="obs-editor">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-base font-semibold text-slate-900" data-testid="obs-editor-nom"><CandidatLink dossierId={dossierId} candidatId={current.candidat_id} icon>{current.candidat.nom} {current.candidat.prenom}</CandidatLink></h4>
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={idx <= 0} onClick={() => setSelectedId(rows[idx - 1].candidat_id)} data-testid="obs-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
              <span data-testid="obs-position">{idx + 1}/{rows.length}</span>
              <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={idx >= rows.length - 1} onClick={() => setSelectedId(rows[idx + 1].candidat_id)} data-testid="obs-next-btn"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
          {date && (
            <div className="flex flex-wrap items-center gap-2" data-testid="obs-signalements">
              {canEdit && !current.candidat.abandon && (
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => setDialog('alerte')} data-testid="obs-alerte-btn"><Siren className="w-3.5 h-3.5 mr-1" /> Émettre une alerte</Button>
              )}
              {canEdit && !current.candidat.abandon && !current.candidat.suspicion_abandon && (
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50" onClick={() => setDialog('suspicion')} data-testid="obs-suspicion-btn"><LogOut className="w-3.5 h-3.5 mr-1" /> Suspicion d'abandon</Button>
              )}
              {current.candidat.suspicion_abandon && !current.candidat.abandon && <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-50 border border-orange-300 text-orange-700" data-testid="obs-suspicion-badge">Suspicion d'abandon signalée le {current.candidat.suspicion_abandon.date}</span>}
              {alertes.filter(a => a.candidat_id === current.candidat_id).map(a => (
                <span key={a.id} className="text-[11px] px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-orange-800" title={`${a.description}${a.mesures ? ' — Mesures : ' + a.mesures : ''}`} data-testid={`obs-alerte-badge-${a.id}`}>Alerte {a.type_label} · {a.date}</span>
              ))}
            </div>
          )}
          <Textarea value={texte} onChange={(e) => onChange(current.candidat_id, e.target.value)} rows={6} disabled={!canEdit}
            placeholder="Observations, remarques, points d'attention… (cliquez sur une chip pour insérer une phrase)" className="text-sm" data-testid="obs-textarea" />
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>{texte.length} car.</span>
            {canEdit && texte && <button type="button" onClick={() => onChange(current.candidat_id, '')} className="flex items-center gap-1 hover:text-slate-600" data-testid="obs-clear-btn"><Eraser className="w-3 h-3" /> Effacer</button>}
          </div>
          {canEdit && chips.length > 0 && (
            <div className="space-y-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Chips rapides — cliquer pour insérer dans l'observation</p>
              {CHIP_CATEGORIES.map(cat => {
                const list = chips.filter(c => c.categorie === cat.key);
                if (!list.length) return null;
                return (
                  <div key={cat.key}>
                    <p className="text-xs font-medium text-slate-700 flex items-center gap-1.5 mb-1"><span className={`w-2 h-2 rounded-full ${cat.dot}`} /> {cat.label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {list.map(chip => {
                        const inserted = texte.toLowerCase().includes(chip.phrase.toLowerCase());
                        return (
                          <button key={chip.id} type="button" onClick={() => toggleChip(chip)} title={chip.phrase}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${inserted ? cat.active : cat.chip}`} data-testid={`obs-chip-${chip.id}`}>
                            {chip.libelle}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
